// Chemin: C:\gestion-copro\src\pages\vigie\KitDemarrageVigiePage.tsx
// Kit de démarrage VIGIE — Checklist interactive "3 docs pour démarrer"
// Session 247 : Création — page dédiée + flag kitDemarrageVu en Firestore
// Accessible via bannière dashboard. Flag Firestore → bannière disparaît définitivement.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';
import {
  Shield,
  FileText,
  CheckCircle2,
  Circle,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Download,
  Sparkles,
  BookOpen,
  Smartphone,
  Mail,
  Globe,
  Building2,
  Loader2,
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface DocPrioritaire {
  id: string;
  numero: number;
  titre: string;
  sousTitre: string;
  ouTrouver: string[];
  pourquoi: string;
  priorite: 'haute' | 'normale';
}

// ============================================
// DONNÉES — 6 documents prioritaires
// ============================================

const DOCS_PRIORITAIRES: DocPrioritaire[] = [
  {
    id: 'pv_ag',
    numero: 1,
    titre: `PV d'Assemblée Générale`,
    sousTitre: 'Le document le plus important pour comprendre votre copropriété',
    ouTrouver: [
      'Courrier annuel envoyé par votre syndic après chaque AG',
      `Espace client en ligne de votre syndic (Matera, Citya, Foncia...)`,
      'Demande directe au syndic par email si vous ne l\'avez pas reçu',
    ],
    pourquoi: `SENTINELLE identifie les membres du conseil syndical, les résolutions votées, le budget approuvé et les travaux prévus.`,
    priorite: 'haute',
  },
  {
    id: 'appel_fonds',
    numero: 2,
    titre: 'Dernier appel de fonds',
    sousTitre: 'Le relevé trimestriel de vos charges',
    ouTrouver: [
      'Courrier trimestriel de votre syndic',
      `Espace client en ligne (rubrique "Mes appels de fonds" ou "Ma comptabilité")`,
      'Votre boîte mail si votre syndic envoie des PDF',
    ],
    pourquoi: `SENTINELLE vérifie les montants, détecte les anomalies comptables et suit l'historique de vos paiements.`,
    priorite: 'haute',
  },
  {
    id: 'dpe',
    numero: 3,
    titre: 'DPE de votre appartement',
    sousTitre: 'Diagnostic de Performance Énergétique',
    ouTrouver: [
      `Acte de vente (remis par votre notaire à l'achat)`,
      'Diagnostiqueur immobilier qui a réalisé le DPE',
      'Promoteur ou constructeur si votre bien est neuf',
    ],
    pourquoi: `SENTINELLE analyse la performance énergétique, détecte les anomalies professionnelles et vous alerte sur les obligations légales.`,
    priorite: 'haute',
  },
  {
    id: 'contrat_syndic',
    numero: 4,
    titre: 'Contrat de syndic',
    sousTitre: 'Le contrat qui définit les honoraires et obligations du syndic',
    ouTrouver: [
      `Annexe au PV d'AG lors du renouvellement du syndic`,
      'Demande directe au syndic — il est obligé de vous le fournir',
    ],
    pourquoi: `SENTINELLE vérifie les honoraires, détecte les clauses potentiellement abusives et contrôle la conformité loi Alur.`,
    priorite: 'normale',
  },
  {
    id: 'reglement',
    numero: 5,
    titre: 'Règlement de copropriété',
    sousTitre: 'La "constitution" de votre immeuble',
    ouTrouver: [
      `Acte de vente remis par votre notaire`,
      `Votre syndic (il est tenu de vous le communiquer)`,
    ],
    pourquoi: `SENTINELLE l'utilise comme référence pour vos droits, obligations et la répartition des charges. Essentiel pour les questions de tantièmes.`,
    priorite: 'normale',
  },
  {
    id: 'courriers',
    numero: 6,
    titre: 'Courriers reçus',
    sousTitre: 'Syndic, promoteur, prestataires — tout ce que vous avez reçu',
    ouTrouver: [
      'Votre boîte mail (recherchez le nom de votre syndic)',
      'Courriers papier à numériser avec votre smartphone',
    ],
    pourquoi: `SENTINELLE analyse le ton, détecte les engagements pris, identifie les délais et prépare vos réponses avec la méthode SCUD.`,
    priorite: 'normale',
  },
];

// ============================================
// COMPOSANT DOC CARD
// ============================================

interface DocCardProps {
  doc: DocPrioritaire;
  depose: boolean;
  onToggle: (id: string) => void;
}

const DocCard: React.FC<DocCardProps> = ({ doc, depose, onToggle }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`rounded-xl border transition-all duration-200 ${
      depose
        ? 'border-emerald-200 bg-emerald-50'
        : doc.priorite === 'haute'
          ? 'border-amber-200 bg-amber-50/50'
          : 'border-gray-200 bg-white'
    }`}>
      {/* En-tête card */}
      <div className="flex items-start gap-3 p-4">
        {/* Numéro + checkbox */}
        <button
          onClick={() => onToggle(doc.id)}
          className="flex-shrink-0 mt-0.5 transition-transform hover:scale-110"
          title={depose ? 'Marquer comme non déposé' : 'Marquer comme déposé'}
        >
          {depose
            ? <CheckCircle2 className="w-6 h-6 text-emerald-500" />
            : <Circle className={`w-6 h-6 ${doc.priorite === 'haute' ? 'text-amber-400' : 'text-gray-300'}`} />
          }
        </button>

        {/* Contenu principal */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
              depose
                ? 'bg-emerald-100 text-emerald-700'
                : doc.priorite === 'haute'
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-gray-100 text-gray-500'
            }`}>
              {depose ? 'Déposé' : doc.priorite === 'haute' ? 'Prioritaire' : `N°${doc.numero}`}
            </span>
            <h3 className={`font-semibold text-sm ${depose ? 'text-emerald-800 line-through opacity-60' : 'text-gray-800'}`}>
              {doc.titre}
            </h3>
          </div>
          <p className={`text-xs mt-0.5 ${depose ? 'text-emerald-600' : 'text-gray-500'}`}>
            {doc.sousTitre}
          </p>
        </div>

        {/* Bouton expand */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 transition-colors"
        >
          {expanded
            ? <ChevronUp className="w-4 h-4" />
            : <ChevronDown className="w-4 h-4" />
          }
        </button>
      </div>

      {/* Détail expandable */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 mt-1 pt-3 space-y-3">
          {/* Où trouver */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Où le trouver
            </p>
            <ul className="space-y-1">
              {doc.ouTrouver.map((source, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                  {source}
                </li>
              ))}
            </ul>
          </div>

          {/* Pourquoi */}
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">
              Ce que SENTINELLE en fait
            </p>
            <p className="text-sm text-blue-800">{doc.pourquoi}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const KitDemarrageVigiePage: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();

  // État local de la checklist (non persisté — c'est un guide, pas un tracker)
  const [deposes, setDeposes] = useState<Set<string>>(new Set());
  const [marquageEnCours, setMarquageEnCours] = useState(false);

  const nbDeposes = deposes.size;
  const nbTotal = DOCS_PRIORITAIRES.length;
  const nbPrioritairesDeposes = DOCS_PRIORITAIRES
    .filter(d => d.priorite === 'haute' && deposes.has(d.id)).length;

  const toggleDoc = (id: string) => {
    setDeposes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Marquer le kit comme vu → flag Firestore → bannière dashboard disparaît
  const marquerCommePret = async () => {
    if (!userProfile?.id) return;
    setMarquageEnCours(true);
    try {
      await updateDoc(doc(db, 'users', userProfile.id), {
        kitDemarrageVu: true,
      });
      navigate('/vigie/sentinelle');
    } catch (error) {
      console.error('[KitDemarrage] Erreur mise à jour flag:', error);
      // On navigue quand même — l'erreur ne bloque pas l'utilisateur
      navigate('/vigie/sentinelle');
    } finally {
      setMarquageEnCours(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">

      {/* ============================================ */}
      {/* EN-TÊTE */}
      {/* ============================================ */}
      <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-white/20 rounded-xl">
            <BookOpen className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Kit de démarrage</h1>
            <p className="text-amber-100 text-sm">Prêt en 3 documents</p>
          </div>
        </div>
        <p className="text-amber-50 text-sm leading-relaxed">
          Vous n'avez pas besoin de tout réunir d'un coup. Commencez par les
          3 documents prioritaires — SENTINELLE sera déjà opérationnelle pour
          vous protéger.
        </p>

        {/* Barre de progression */}
        {nbDeposes > 0 && (
          <div className="mt-4">
            <div className="flex justify-between text-xs text-amber-100 mb-1.5">
              <span>{nbDeposes} document{nbDeposes > 1 ? 's' : ''} prêt{nbDeposes > 1 ? 's' : ''}</span>
              <span>{nbTotal - nbDeposes} restant{nbTotal - nbDeposes > 1 ? 's' : ''}</span>
            </div>
            <div className="bg-white/20 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all duration-500"
                style={{ width: `${(nbDeposes / nbTotal) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* ENCADRÉ — 3 docs suffisent pour démarrer */}
      {/* ============================================ */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-emerald-800">
            3 documents suffisent pour démarrer
          </p>
          <p className="text-sm text-emerald-700 mt-0.5">
            PV d'AG, appel de fonds et DPE — déposez-les dans SENTINELLE et elle
            commence immédiatement à travailler pour vous.
          </p>
        </div>
      </div>

      {/* ============================================ */}
      {/* CHECKLIST DOCUMENTS */}
      {/* ============================================ */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Documents à rassembler — cliquez pour voir où les trouver
        </h2>
        <div className="space-y-2">
          {DOCS_PRIORITAIRES.map(d => (
            <DocCard
              key={d.id}
              doc={d}
              depose={deposes.has(d.id)}
              onToggle={toggleDoc}
            />
          ))}
        </div>
      </div>

      {/* ============================================ */}
      {/* COMMENT PRÉPARER — conseils pratiques */}
      {/* ============================================ */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <h2 className="font-semibold text-gray-800 flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-amber-500" />
          Comment préparer vos documents
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Numérique */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-blue-500" />
              <p className="text-sm font-semibold text-gray-700">Déjà numérique</p>
            </div>
            <ul className="space-y-1.5 text-sm text-gray-600">
              <li className="flex items-start gap-1.5">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                PDF reçu par email → téléchargez-le directement
              </li>
              <li className="flex items-start gap-1.5">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                Email sans pièce jointe → Imprimer → Enregistrer en PDF
              </li>
              <li className="flex items-start gap-1.5">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                Espace syndic en ligne → téléchargez le PDF
              </li>
            </ul>
          </div>

          {/* Papier */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Smartphone className="w-4 h-4 text-amber-500" />
              <p className="text-sm font-semibold text-gray-700">Version papier</p>
            </div>
            <ul className="space-y-1.5 text-sm text-gray-600">
              <li className="flex items-start gap-1.5">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                App smartphone : Adobe Scan, Microsoft Lens, Google Drive
              </li>
              <li className="flex items-start gap-1.5">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                Ces apps redressent le doc et génèrent un PDF propre
              </li>
              <li className="flex items-start gap-1.5">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                Évitez la photo simple — souvent illisible pour l'IA
              </li>
            </ul>
          </div>
        </div>

        {/* Astuce qualité */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
          <span className="text-amber-500 font-bold text-sm flex-shrink-0">!</span>
          <p className="text-sm text-amber-800">
            Un scan flou ou sombre réduira la qualité de l'analyse. Prenez
            30 secondes de plus pour un résultat net — SENTINELLE vous l'affichera
            avec un badge de qualité de lecture.
          </p>
        </div>
      </div>

      {/* ============================================ */}
      {/* COMMENT DÉPOSER */}
      {/* ============================================ */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
        <h2 className="font-semibold text-gray-800 flex items-center gap-2">
          <Shield className="w-4 h-4 text-amber-500" />
          Comment déposer dans Copro Protect
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
            <p className="text-sm font-bold text-amber-800 mb-1">
              Via SENTINELLE (recommandé)
            </p>
            <p className="text-sm text-amber-700">
              Glissez-déposez votre fichier directement dans le chat. SENTINELLE
              analyse immédiatement et vous propose de l'archiver.
            </p>
          </div>
          <div className="border border-gray-200 bg-gray-50 rounded-lg p-3">
            <p className="text-sm font-bold text-gray-700 mb-1">
              Via Mes documents
            </p>
            <p className="text-sm text-gray-600">
              Menu Mes documents → Ajouter un document → sélectionnez la
              catégorie. L'analyse SENTINELLE se déclenche automatiquement.
            </p>
          </div>
        </div>

        <p className="text-xs text-gray-400">
          Formats acceptés : PDF (recommandé), JPG, PNG · Taille max : 10 Mo · Stockage : 2 Go
        </p>
      </div>

      {/* ============================================ */}
      {/* PDF TÉLÉCHARGEABLE */}
      {/* ============================================ */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <FileText className="w-5 h-5 text-gray-500" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">
              Guide complet en PDF
            </p>
            <p className="text-xs text-gray-500">À imprimer ou à garder sur votre téléphone</p>
          </div>
        </div>
        <a
          href="https://firebasestorage.googleapis.com/v0/b/gestion-copro-c6ec2.firebasestorage.app/o/templates%2FKit_Demarrage_VIGIE_Copro_Protect.pdf?alt=media&token=ae9cc44b-c74c-44ac-b903-58df00d53bc3"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors flex-shrink-0"
        >
          <Download className="w-4 h-4" />
          Télécharger
        </a>
      </div>

      {/* ============================================ */}
      {/* CTA — Je démarre */}
      {/* ============================================ */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-5 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div>
            <p className="font-bold text-lg">
              {nbPrioritairesDeposes >= 3
                ? `Vous êtes prêt — SENTINELLE vous attend !`
                : `Vous pouvez démarrer dès maintenant`}
            </p>
            <p className="text-amber-100 text-sm mt-0.5">
              {nbPrioritairesDeposes >= 3
                ? `Vous avez les 3 documents prioritaires. SENTINELLE est prête.`
                : `Déposez votre premier document dans SENTINELLE — elle vous guide pour la suite.`}
            </p>
          </div>
          <button
            onClick={marquerCommePret}
            disabled={marquageEnCours}
            className="sm:ml-auto flex items-center gap-2 bg-white text-amber-700 px-5 py-2.5 rounded-lg font-bold hover:bg-amber-50 transition-colors whitespace-nowrap disabled:opacity-70 shadow-sm"
          >
            {marquageEnCours
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <ArrowRight className="w-4 h-4" />
            }
            {marquageEnCours ? 'Chargement...' : `J'ai compris, je démarre`}
          </button>
        </div>
      </div>

    </div>
  );
};

export default KitDemarrageVigiePage;
